import React, { useState } from 'react';
import { FiPlus, FiX } from 'react-icons/fi';

export function PainPointSelectionStep({ formData, onChange, onToggleArray }) {
  const [newPainPoint, setNewPainPoint] = useState('');

  const painPointsByVertical = {
    // Self-Service Operations
    parking: [
      'Revenue leakage from cash handling',
      'Customer frustration with limited payment options',
      'Integration challenges with PARCS systems',
      'PCI compliance complexity',
      'High maintenance costs for payment terminals',
      'Difficulty managing multiple payment processors'
    ],
    fuel: [
      'Forecourt system integration complexity',
      'Fleet card management challenges',
      'Outdoor payment reliability issues',
      'Multi-site payment orchestration',
      'EMV compliance requirements',
      'Fraud prevention at pump'
    ],
    vending: [
      'Terminal downtime impacting revenue',
      'Limited payment method acceptance',
      'Complex integrations with vending equipment',
      'Remote monitoring and maintenance costs',
      'Cashless payment adoption barriers',
      'Multiple vendor management overhead'
    ],
    carwash: [
      'Weather-resistant payment solutions needed',
      'Integration with wash control systems',
      'Customer experience at payment point',
      'Subscription and loyalty program management',
      'Mobile payment adoption challenges',
      'Equipment vandalism and security concerns'
    ],
    laundry: [
      'Card reader reliability in humid environments',
      'Mobile app integration challenges',
      'Value-add services payment processing',
      'Multi-location management complexity',
      'Customer balance management',
      'Equipment integration limitations'
    ],
    evcharging: [
      'Complex pricing and billing structures',
      'Network interoperability challenges',
      'Fleet management payment solutions',
      'Real-time authorization requirements',
      'Membership and subscription handling',
      'Grid integration and demand management'
    ],
    transit: [
      'Fare collection system modernization',
      'Multi-modal payment integration',
      'Real-time validation requirements',
      'Accessibility compliance needs',
      'Bulk transaction processing',
      'Revenue sharing between operators'
    ],
    amusement: [
      'Cashless ecosystem implementation',
      'Prize redemption payment flows',
      'Age verification for certain games',
      'Group and party payment management',
      'Seasonal business fluctuations',
      'Integration with loyalty and rewards'
    ],
    
    // Food & Beverage
    qsr: [
      'Long transaction times affecting throughput',
      'Integration with POS and kitchen systems',
      'Drive-thru payment optimization',
      'Mobile ordering and payment flows',
      'Loyalty program integration',
      'Multi-tender transaction handling'
    ],
    'specialty-food': [
      'Premium product margin protection',
      'Customer experience expectations',
      'Inventory management integration',
      'Subscription box payment handling',
      'Age verification for alcohol sales',
      'Seasonal demand fluctuations'
    ],
    
    // Retail & Commerce
    'fashion-apparel': [
      'Omnichannel payment consistency',
      'Pop-up and temporary location challenges',
      'High-value transaction security',
      'Return and exchange processing',
      'Seasonal inventory payment flows',
      'Customer loyalty and rewards integration'
    ],
    'beauty-cosmetics': [
      'Service booking payment integration',
      'Product and service bundling complexity',
      'Subscription and membership management',
      'High-touch customer experience requirements',
      'Inventory and appointment system integration',
      'Tip and gratuity handling'
    ],
    'electronics-tech': [
      'High-value transaction security',
      'Warranty and service plan processing',
      'Trade-in and credit handling',
      'Technical support payment integration',
      'B2B and consumer payment separation',
      'Rapid technology refresh cycles'
    ],
    'home-garden': [
      'Large item delivery payment coordination',
      'Seasonal demand variability',
      'Installation service payment bundling',
      'B2B contractor payment handling',
      'Return and exchange complexity',
      'Multi-location inventory management'
    ],
    'pet-services': [
      'Service appointment payment integration',
      'Emergency care payment processing',
      'Subscription and recurring service billing',
      'Insurance and reimbursement handling',
      'Product and service bundling',
      'Multi-pet family account management'
    ],
    'sporting-goods': [
      'Seasonal inventory payment challenges',
      'Equipment rental payment processing',
      'Team and bulk order management',
      'Outdoor event payment solutions',
      'Equipment customization payment flows',
      'Membership and club integration'
    ],
    'health-wellness': [
      'Insurance and HSA payment integration',
      'Subscription and recurring billing',
      'Age verification requirements',
      'Professional consultation payment processing',
      'Inventory and prescription integration',
      'Privacy and compliance requirements'
    ],
    'jewelry-accessories': [
      'High-value transaction security',
      'Custom order and sizing payment flows',
      'Authentication and certification costs',
      'Insurance and appraisal integration',
      'Consignment payment handling',
      'Luxury customer experience expectations'
    ],
    'books-stationery': [
      'Educational institution payment integration',
      'Subscription and membership billing',
      'Digital and physical product bundling',
      'Bulk order and institutional sales',
      'Author event and signing payment processing',
      'Seasonal academic demand fluctuations'
    ],
    'hobby-crafts': [
      'Class and workshop payment processing',
      'Subscription box and kit billing',
      'Community event payment coordination',
      'Maker space membership management',
      'Custom project payment flows',
      'Seasonal and project-based demand'
    ]
  };

  const commonPainPoints = [
    'High payment processing fees',
    'Complex integration requirements',
    'Security and compliance concerns',
    'Poor customer payment experience',
    'Limited payment method options',
    'Inadequate reporting and analytics',
    'Unreliable payment infrastructure',
    'Lack of technical support',
    'Slow settlement times',
    'Difficulty scaling payment operations'
  ];

  const handleAddCustomPainPoint = () => {
    if (newPainPoint.trim() && !formData.customPainPoints.includes(newPainPoint.trim())) {
      const updatedCustomPainPoints = [...formData.customPainPoints, newPainPoint.trim()];
      onChange('customPainPoints', updatedCustomPainPoints);
      setNewPainPoint('');
    }
  };

  const handleRemoveCustomPainPoint = (painPoint) => {
    const updatedCustomPainPoints = formData.customPainPoints.filter(p => p !== painPoint);
    onChange('customPainPoints', updatedCustomPainPoints);
  };

  const relevantPainPoints = formData.vertical ? painPointsByVertical[formData.vertical] || [] : [];

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Pain Point Selection</h2>
        <p className="text-gray-600 mb-8">
          Identify the key challenges your target audience faces. This will help create content that directly addresses their needs and positions TNS solutions as the answer.
        </p>
      </div>

      {/* Vertical-Specific Pain Points */}
      {relevantPainPoints.length > 0 && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {formData.vertical ? `${formData.vertical.charAt(0).toUpperCase() + formData.vertical.slice(1)} Industry Pain Points` : 'Industry-Specific Pain Points'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {relevantPainPoints.map((painPoint) => (
              <label
                key={painPoint}
                className={`relative rounded-lg border p-4 cursor-pointer transition-colors ${
                  formData.selectedPainPoints.includes(painPoint)
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    checked={formData.selectedPainPoints.includes(painPoint)}
                    onChange={() => onToggleArray('selectedPainPoints', painPoint)}
                    className="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300 rounded mt-1"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-900">
                    {painPoint}
                  </span>
                </div>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Common Pain Points */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Common Payment Pain Points</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {commonPainPoints.map((painPoint) => (
            <label
              key={painPoint}
              className={`relative rounded-lg border p-4 cursor-pointer transition-colors ${
                formData.selectedPainPoints.includes(painPoint)
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-start">
                <input
                  type="checkbox"
                  checked={formData.selectedPainPoints.includes(painPoint)}
                  onChange={() => onToggleArray('selectedPainPoints', painPoint)}
                  className="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300 rounded mt-1"
                />
                <span className="ml-3 text-sm font-medium text-gray-900">
                  {painPoint}
                </span>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Custom Pain Points */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Custom Pain Points</h3>
        <p className="text-sm text-gray-500 mb-4">Add specific pain points that are unique to your target audience or campaign.</p>
        
        {/* Add new pain point */}
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={newPainPoint}
            onChange={(e) => setNewPainPoint(e.target.value)}
            placeholder="Enter a custom pain point..."
            className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
            onKeyPress={(e) => e.key === 'Enter' && handleAddCustomPainPoint()}
          />
          <button
            onClick={handleAddCustomPainPoint}
            disabled={!newPainPoint.trim()}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center"
          >
            <FiPlus className="w-4 h-4" />
          </button>
        </div>

        {/* Display custom pain points */}
        {formData.customPainPoints.length > 0 && (
          <div className="space-y-2">
            {formData.customPainPoints.map((painPoint, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-md"
              >
                <span className="text-sm font-medium text-gray-900">{painPoint}</span>
                <button
                  onClick={() => handleRemoveCustomPainPoint(painPoint)}
                  className="text-red-600 hover:text-red-800"
                >
                  <FiX className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Summary */}
      {(formData.selectedPainPoints.length > 0 || formData.customPainPoints.length > 0) && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <h4 className="text-sm font-medium text-blue-900 mb-2">Selected Pain Points Summary</h4>
          <p className="text-sm text-blue-700">
            You have selected {formData.selectedPainPoints.length + formData.customPainPoints.length} pain points that will be addressed in your marketing content.
          </p>
        </div>
      )}
    </div>
  );
}